import { Formik } from "formik";
import { isEqual } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FiEdit } from "react-icons/fi";
import { RiSave2Fill } from "react-icons/ri";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import { ExportIconButton } from "../../../components/ExportButton";
import { apiService } from "../../../services/apiService";
import CustomTabs from "../../../components/CustomTabs";
import FileUpload from "../../../components/FileUpload";
import GoogleMapInput from "../../../components/GoogleMapInput";
import Input from "../../../components/input";
import PermissionWrapper from "../../../components/PermissionWrapper";
import PhoneNumberInput from "../../../components/PhoneNumberInput";
import Select from "../../../components/Select";
import { buildPhoneNumberSchema, normalizePhoneNumberValue } from "../../../pages/ThreePL/shared/phoneValidation";
import { fetchFuelCompaniesDropdown } from "../../../store/Slices/dropdownSlice";
import { fetchFuelStationDetails, updateFuelRate, updateFuelStation } from "../../../store/Slices/FuelStationSlice";
import '../../../styles/FuelStation.css';
import { AppMainModules, FuelStationPermission, FuelSubModules } from "../../../utils/app-constant";
import { formatOnlyDate, formats, fuelStationInitialValues, hasFullPermission, modules } from "../../../utils/helper";
import SpinnerLoader from "../../../utils/SpinnerLoader";
import {
    activeDetailsIcon, activeHistoryIcon,
    detailsIcon, historyIcon,
} from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";
import FuelStationHistory from "./FuelStationHistory";



const fuelRateUpdateSchema = Yup.object().shape({
    changed_at: Yup.date()
        .typeError("Effective Date is required")
        .required("Effective Date is required"),
    rates: Yup.array()
        .of(
            Yup.object().shape({
                fuel_type: Yup.string().required("Fuel type is required"),
                price: Yup.number()
                    .transform((value, originalValue) =>
                        String(originalValue ?? "").trim() === "" ? undefined : value
                    )
                    .typeError("Rate must be a number")
                    .min(0, "Rate must be zero or positive")
                    .required("Rate is required"),
            })
        )
        .min(1, "At least one fuel rate is required")
        .required("Fuel rates are required"),
});

const fuelStationDetailsSchema = Yup.object().shape({
    name: Yup.string().required("Station Name is required"),
    contact: buildPhoneNumberSchema("Contact is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    location_info: Yup.object().shape({
        station_point: Yup.string().required("Location is required"),
        station_latitude: Yup.mixed().optional().nullable(),
        station_longitude: Yup.mixed().optional().nullable(),
    }),
    fuel_company_id: Yup.number().required("Fuel Company is required"),
    contract_start_at: Yup.date().required("Contract Start Date is required"),
    contract_end_at: Yup.date()
        .min(Yup.ref("contract_start_at"), "Contract End must be after Start")
        .required("Contract End Date is required"),
});

const FuelStationDetails = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const { fuelStationDetails, fuelStationDetailsLoading, fuelStationUpdateLoading, fuelStationRateUpdateLoading } = useSelector((state) => state.fuelStation);
    const { loading: dropdownLoading, fuelCompaniesDropdownData } = useSelector((state) => state.dropdowns);

    const [activeTab, setActiveTab] = useState("detail");
    const [isRateEditing, setIsRateEditing] = useState(false);
    const [initialValues, setInitialValues] = useState({
        ...fuelStationInitialValues,
        status: "active"
    });

    
    const [ratesExportLoading, setRatesExportLoading] = useState(null);

    const downloadExportedFile = (filePath, fileName) => {
        const fileUrl = /^https?:\/\//i.test(filePath)
            ? filePath
            : `${process.env.REACT_APP_API_FILES_URL}${filePath}`;
        const link = document.createElement("a");
        link.href = fileUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFuelRatesExport = async (type) => {
        if (ratesExportLoading) return;
        const stationId = Number(id);
        if (!stationId) {
            errorNotify("Fuel Station ID is missing");
            return;
        }

        setRatesExportLoading(type);
        try {
            const payload = {
                fuel_station_id: stationId,
            };

            const response =
                type === "pdf"
                    ? await apiService.exportFuelStationRatesPdf(payload)
                    : await apiService.exportFuelStationRatesExcel(payload);

            const responseData = response?.data ?? response;
            const filePath =
                responseData?.url ||
                responseData?.file ||
                (typeof responseData === "string" ? responseData : null);

            if (!filePath) {
                throw new Error(
                    `${type.toUpperCase()} was generated but no file was returned`
                );
            }

            const safeStationName = (fuelStationDetails?.name || `Station_${stationId}`).replace(
                /[^a-zA-Z0-9_-]/g,
                "_"
            );

            downloadExportedFile(
                filePath,
                `${safeStationName}-rate-history.${type === "pdf" ? "pdf" : "xlsx"}`
            );
            successNotify(
                `Fuel rate history ${type.toUpperCase()} downloaded successfully`
            );
        } catch (error) {
            const msg = typeof error === "string" ? error : error?.message;
            errorNotify(msg || `Failed to download fuel rate history ${type.toUpperCase()}`);
        } finally {
            setRatesExportLoading(null);
        }
    };

    const canUpdate = hasFullPermission({
        permissionSlug: FuelStationPermission.Update,
        moduleSlug: AppMainModules.fuelManagement,
        menuSlug: FuelSubModules.FuelStations
    });

    useEffect(() => {
        dispatch(fetchFuelStationDetails(id));
        dispatch(fetchFuelCompaniesDropdown());
    }, [id, dispatch]);

    useEffect(() => {
        if (fuelStationDetails) {
            const d = fuelStationDetails;
            setInitialValues({
                name: d.name || "",
                contact: normalizePhoneNumberValue(d.contact || ""),
                email: d.email || "",
                location_info: {
                    station_point: d.location || "",
                    station_latitude: d.lat || 0,
                    station_longitude: d.lng || 0
                },
                fuel_company_id: d.fuel_company_id || "",
                station_image: d.station_image ? [{ fileName: d.station_image }] : [],
                station_document: d.station_document ? [{ fileName: d.station_document }] : [],
                contract_start_at: d.contract_start_at ? moment(d.contract_start_at).format("YYYY-MM-DD") : "",
                contract_end_at: d.contract_end_at ? moment(d.contract_end_at).format("YYYY-MM-DD") : "",
                status: d.status || "active",
                notes: d.notes || ""
            });
        }
    }, [fuelStationDetails]);

    const stationTabs = [
        {
            key: "detail",
            label: "Station Details",
            icon: activeTab === "detail" ? activeDetailsIcon : detailsIcon,
        },
        {
            key: "history",
            label: "Rate History",
            icon: activeTab === "history" ? activeHistoryIcon : historyIcon,
        }
    ];

    if (fuelStationDetailsLoading) {
        return <SpinnerLoader />;
    }

    return (
        <>
            <Breadcrumbs values={["Fuel Management", "Fuel Station Details"]} />

            <Formik
                initialValues={initialValues}
                validationSchema={fuelStationDetailsSchema}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        const getPayload = (val) => ({
                            fuel_station_id: Number(id),
                            name: val.name,
                            contact: val.contact,
                            email: val.email,
                            location: val.location_info?.station_point || "",
                            lat: val.location_info?.station_latitude || 0,
                            lng: val.location_info?.station_longitude || 0,
                            fuel_company_id: Number(val.fuel_company_id),
                            station_image: Array.isArray(val.station_image) ? val.station_image[0]?.fileName : val.station_image,
                            station_document: Array.isArray(val.station_document) ? val.station_document[0]?.fileName : val.station_document,
                            contract_start_at: val.contract_start_at,
                            contract_end_at: val.contract_end_at,
                            status: val.status,
                            notes: val.notes,
                        });

                        const currentPayload = getPayload(values);
                        const initialPayload = getPayload(initialValues);

                        const changedKeys = Object.keys(currentPayload).filter((key) => {
                            if (key === 'fuel_station_id') return false;
                            return !isEqual(currentPayload[key], initialPayload[key]);
                        });

                        const payload = {
                            ...currentPayload,
                            updatedKeys: changedKeys,
                        };

                        await dispatch(updateFuelStation(payload)).unwrap();
                        successNotify("Fuel station updated successfully.");
                        dispatch(fetchFuelStationDetails(id));
                    } catch (error) {
                        errorNotify(typeof error === 'string' ? error : error?.message || "Failed to update fuel station.");
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({
                    values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, validateForm, setTouched
                }) => (
                    <>
                        <div className="page_header">
                            <h2 className="mb-2">Fuel Station Details</h2>

                            <PermissionWrapper
                                module={FuelSubModules.FuelStations}
                                permission={FuelStationPermission.Update}
                                mainModule={AppMainModules.fuelManagement}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <Button
                                        cancelBtn={() => navigate(-1)}
                                        loading={fuelStationUpdateLoading}
                                        disabled={fuelStationUpdateLoading}
                                        submitText={fuelStationUpdateLoading ? "Saving..." : "Save Details"}
                                        type="button"
                                        shouldRow={false}
                                        isShowSubmitBtn={true}
                                        submitBtn={handleSubmit}
                                    />
                                </div>
                            </PermissionWrapper>

                        </div>
                        <div className="page_container">
                            <Row>
                                <Col md={8}>
                                    <div className="trip_details_main_div">
                                        <div>
                                            <h6>
                                                Station ID: <span>{`FS-${fuelStationDetails?.id || "N/A"}`}</span>
                                            </h6>
                                        </div>

                                        <CustomTabs
                                            tabs={stationTabs}
                                            activeTab={activeTab}
                                            setActiveTab={setActiveTab}
                                        />
                                    </div>

                                    {activeTab === "detail" && (
                                        <>
                                            <div className="content_box">
                                                <h6 className="mb-2">General Information</h6>
                                                <Row>
                                                    <Col md={6}>
                                                        <Input label="Station Name" placeholder="Station Name" name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} error={errors.name} touched={touched.name} />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Fuel Company"
                                                            placeholder="Select Company"
                                                            options={fuelCompaniesDropdownData.map(c => ({ label: c.name, value: c.id }))}
                                                            value={fuelCompaniesDropdownData?.map(c => ({ label: c.name, value: c.id })).find(o => o.value === values.fuel_company_id)}
                                                            loading={dropdownLoading}
                                                            name="fuel_company_id"
                                                            onChange={(opt) => setFieldValue('fuel_company_id', opt ? opt.value : '')}
                                                            onBlur={() => handleBlur({ target: { name: 'fuel_company_id' } })}
                                                            error={errors.fuel_company_id}
                                                            touched={touched.fuel_company_id}
                                                        />
                                                    </Col>
                                                     <Col md={6}>
                                                         <PhoneNumberInput
                                                             label="Contact Number"
                                                             name="contact"
                                                             placeholder="923XXXXXXXXX"
                                                             value={values.contact}
                                                             setFieldValue={setFieldValue}
                                                             onBlur={handleBlur}
                                                             error={errors.contact}
                                                             touched={touched.contact}
                                                         />
                                                     </Col>
                                                    <Col md={6}>
                                                        <Input label="Email Address" placeholder="station@example.com" name="email" value={values.email} onChange={handleChange} onBlur={handleBlur} error={errors.email} touched={touched.email} />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select
                                                            label="Status"
                                                            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
                                                            value={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }].find(o => o.value === values.status)}
                                                            name="status"
                                                            onChange={(opt) => setFieldValue('status', opt ? opt.value : 'active')}
                                                        />
                                                    </Col>
                                                    <Col md={12}>
                                                        <GoogleMapInput
                                                            label="Station Location"
                                                            name="location_info"
                                                            value={values.location_info}
                                                            setFieldValue={setFieldValue}
                                                            error={errors.location_info?.station_point || errors.location_info}
                                                            touched={touched.location_info?.station_point || touched.location_info}
                                                            fieldPrefix="station"
                                                            required={true}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                            <Formik
                                                initialValues={{
                                                    changed_at: "",
                                                    rates: fuelStationDetails?.current_rates?.map(r => ({ fuel_type: r.fuel_type, price: r.price })) || []
                                                }}
                                                enableReinitialize={true}
                                                validationSchema={fuelRateUpdateSchema}
                                                onSubmit={async (rateValues) => {
                                                    const hasDateChanged = !!rateValues.changed_at;
                                                    const changedRates = rateValues.rates.filter(rate => {
                                                        const original = fuelStationDetails?.current_rates?.find(r => r.fuel_type === rate.fuel_type);
                                                        return Number(rate.price) !== Number(original?.price);
                                                    });

                                                    if (hasDateChanged || changedRates.length > 0) {
                                                        try {
                                                            const ratesToSend = hasDateChanged ? rateValues.rates : changedRates;
                                                            const payload = {
                                                                fuel_station_id: Number(id),
                                                                changed_at: rateValues.changed_at,
                                                                rates: ratesToSend.map(r => ({
                                                                    fuel_type: r.fuel_type,
                                                                    price: Number(r.price)
                                                                }))
                                                            };
                                                            await dispatch(updateFuelRate(payload)).unwrap();
                                                            successNotify("Fuel rates updated successfully.");
                                                            dispatch(fetchFuelStationDetails(id));
                                                            setIsRateEditing(false);
                                                        } catch (error) {
                                                            errorNotify(typeof error === 'string' ? error : error?.message || "Failed to update fuel rates.");
                                                        }
                                                    } else {
                                                        setIsRateEditing(false);
                                                    }
                                                }}
                                            >
                                                {({
                                                    values: rateValues,
                                                    errors: rateErrors,
                                                    touched: rateTouched,
                                                    handleChange: handleRateChange,
                                                    handleBlur: handleRateBlur,
                                                    handleSubmit: handleRateSubmit
                                                }) => {
                                                    const hasPendingRateChanges = rateValues.rates.some((rate) => {
                                                        const original = fuelStationDetails?.current_rates?.find(
                                                            (currentRate) => currentRate.fuel_type === rate.fuel_type
                                                        );
                                                        return Number(rate.price) !== Number(original?.price);
                                                    });

                                                    return (
                                                    <div className="content_box">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <h6 className="m-0">Fuel Rates </h6>
                                                            <PermissionWrapper
                                                                module={FuelSubModules.FuelStations}
                                                                permission={FuelStationPermission.Update}
                                                                mainModule={AppMainModules.fuelManagement}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="edit_btn_icon"
                                                                    disabled={fuelStationRateUpdateLoading}
                                                                    onClick={() => {
                                                                        if (isRateEditing) {
                                                                            if (!hasPendingRateChanges && !rateValues.changed_at) {
                                                                                setIsRateEditing(false);
                                                                                return;
                                                                            }
                                                                            handleRateSubmit();
                                                                        } else {
                                                                            setIsRateEditing(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    {fuelStationRateUpdateLoading ? (
                                                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                                                    ) : isRateEditing ? (
                                                                        <RiSave2Fill size={20} color="#1447b1" />
                                                                    ) : (
                                                                        <FiEdit size={18} color="#1447b1" />
                                                                    )}
                                                                </button>
                                                            </PermissionWrapper>

                                                        </div>
                                                        <Row>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Effective Date"
                                                                    placeholder="Select Effective Date"
                                                                    type="date"
                                                                    name="changed_at"
                                                                    value={rateValues.changed_at}
                                                                    onChange={handleRateChange}
                                                                    onBlur={handleRateBlur}
                                                                    error={rateErrors.changed_at}
                                                                    touched={rateTouched.changed_at}
                                                                    disabled={!isRateEditing}
                                                                />
                                                            </Col>
                                                            {rateValues.rates.map((rate, index) => (
                                                                <Col md={6} key={index}>
                                                                    <Input
                                                                        label={`${rate.fuel_type.charAt(0).toUpperCase() + rate.fuel_type.slice(1).replace('_', ' ')} Rate`}
                                                                        placeholder="0.00"
                                                                        type="number"
                                                                        name={`rates[${index}].price`}
                                                                        value={rate.price}
                                                                        onChange={handleRateChange}
                                                                        onBlur={handleRateBlur}
                                                                        error={rateErrors?.rates?.[index]?.price}
                                                                        touched={rateTouched?.rates?.[index]?.price}
                                                                        disabled={!isRateEditing}
                                                                    />
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    </div>
                                                )}}
                                            </Formik>

                                            <div className="content_box">
                                                <h6 className="mb-2">Contracts</h6>
                                                <Row>
                                                    <Col md={6}>
                                                        <Input
                                                            label="Contract Start Date"
                                                            type="date"
                                                            name="contract_start_at"
                                                            value={values.contract_start_at}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={errors.contract_start_at}
                                                            touched={touched.contract_start_at}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Input
                                                            label="Contract End Date"
                                                            type="date"
                                                            name="contract_end_at"
                                                            value={values.contract_end_at}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={errors.contract_end_at}
                                                            touched={touched.contract_end_at}
                                                        />
                                                    </Col>
                                                    <Col md={12}>
                                                        <div className="upload_group mt-3">
                                                            <label>Contract Document</label>
                                                            <FileUpload
                                                                files={values.station_document}
                                                                setFiles={(newFiles) => setFieldValue('station_document', typeof newFiles === 'function' ? newFiles(values.station_document) : newFiles)}
                                                                destination="fuel-stations"
                                                                maxFiles={1}
                                                                disabled={!canUpdate}
                                                            />
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>

                                            <div className="content_box">
                                                <h6 className="mb-2">Media</h6>
                                                <Row>
                                                    <Col md={12}>
                                                        <div className="upload_group">
                                                            <label>Station Image</label>
                                                            <FileUpload
                                                                files={values.station_image}
                                                                setFiles={(newFiles) => setFieldValue('station_image', typeof newFiles === 'function' ? newFiles(values.station_image) : newFiles)}
                                                                destination="fuel-stations"
                                                                maxFiles={1}
                                                                disabled={!canUpdate}
                                                            />
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </>
                                    )}

                                    {activeTab === "history" && (
                                        <div className="content_box">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="mb-0">Fuel Rate History</h6>
                                                <div className="d-flex align-items-center gap-2">
                                                    <ExportIconButton
                                                        type="pdf"
                                                        onClick={() => handleFuelRatesExport("pdf")}
                                                        loading={ratesExportLoading === "pdf"}
                                                        disabled={
                                                            !fuelStationDetails?.history_rates?.length ||
                                                            Boolean(ratesExportLoading)
                                                        }
                                                        title={
                                                            !fuelStationDetails?.history_rates?.length
                                                                ? "No rate history to export"
                                                                : undefined
                                                        }
                                                    />
                                                    <ExportIconButton
                                                        type="excel"
                                                        onClick={() => handleFuelRatesExport("excel")}
                                                        loading={ratesExportLoading === "excel"}
                                                        disabled={
                                                            !fuelStationDetails?.history_rates?.length ||
                                                            Boolean(ratesExportLoading)
                                                        }
                                                        title={
                                                            !fuelStationDetails?.history_rates?.length
                                                                ? "No rate history to export"
                                                                : undefined
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <div className="table-responsive">
                                                <table className="table custom-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Fuel Type</th>
                                                            <th>Old Price (PKR)</th>
                                                            <th>Price (PKR)</th>
                                                            <th>Effective Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {fuelStationDetails?.history_rates?.length > 0 ? (
                                                            fuelStationDetails.history_rates.map((history, idx) => (
                                                                <tr key={idx}>
                                                                    <td className="text-capitalize">{history?.fuel_type.replace('_', ' ')}</td>
                                                                    <td>₨ {history?.old_price}</td>
                                                                    <td>₨ {history?.price}</td>
                                                                    <td>{formatOnlyDate(history?.changed_at)}</td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="4" className="text-center">No rate history available</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </Col>

                                <Col md={4}>
                                    <FuelStationHistory />
                                    <div className='trip_notes_main_div' style={{ minHeight: "fit-content", height: "auto", padding: "15px", marginTop: "15px" }}>
                                        <h5 className="mb-2">Notes</h5>
                                        <ReactQuill
                                            placeholder={'Write notes here...'}
                                            modules={modules}
                                            theme="snow"
                                            value={values.notes || ''}
                                            formats={formats}
                                            onChange={(content) => setFieldValue('notes', content)}
                                            className='react_quill_input notes_quill_input quill-small-font trip_quill_input'
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </>
                )}
            </Formik>
        </>
    );
};

export default FuelStationDetails;
